import { useEffect, useState } from "react";
import { MapPin, Search, Wind, Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog } from "lucide-react";

const CODES = [
  [[0], "Clear", Sun],
  [[1, 2], "Partly cloudy", Cloud],
  [[3], "Overcast", Cloud],
  [[45, 48], "Fog", CloudFog],
  [[51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82], "Rain", CloudRain],
  [[71, 73, 75, 77, 85, 86], "Snow", CloudSnow],
  [[95, 96, 99], "Storm", CloudLightning],
];
function wx(code) {
  for (const [codes, label, Icon] of CODES) if (codes.includes(code)) return { label, Icon };
  return { label: "—", Icon: Cloud };
}
const day = (d) => new Date(d).toLocaleDateString("en-US", { weekday: "short" });

function fxFor(label) {
  if (label === "Clear") return "clear";
  if (label === "Rain") return "rain";
  if (label === "Snow") return "snow";
  if (label === "Storm") return "storm";
  if (label === "Fog") return "fog";
  return "clouds";
}

function WeatherFX({ fx }) {
  if (fx === "clear") return (<div className="wx-layers"><span className="wx-rays" /><span className="wx-sun" /></div>);
  if (fx === "clouds") return (
    <div className="wx-layers">
      {[[20, 26, 0], [90, 40, -8], [150, 20, -16]].map(([top, dur, delay], i) => (
        <span key={i} className="wx-cloud" style={{ top, left: "-20%", animationDuration: `${dur}s`, animationDelay: `${delay}s` }} />
      ))}
    </div>
  );
  if (fx === "fog") return (
    <div className="wx-layers">
      {[10, 60, 110].map((top, i) => (
        <span key={i} className="wx-fogband" style={{ top, animationDuration: `${18 + i * 6}s`, animationDelay: `${-i * 4}s` }} />
      ))}
    </div>
  );
  if (fx === "snow") return (
    <div className="wx-layers">
      {Array.from({ length: 20 }).map((_, i) => (
        <span key={i} className="wx-flake" style={{ left: `${(i * 5.1) % 100}%`, animationDuration: `${4 + (i % 5)}s`, animationDelay: `${-(i % 7) * 0.6}s`, transform: `scale(${0.5 + (i % 4) * 0.25})` }} />
      ))}
    </div>
  );
  // rain + storm
  return (
    <div className="wx-layers">
      {Array.from({ length: 26 }).map((_, i) => (
        <span key={i} className={`wx-drop${i % 6 === 0 ? " red" : ""}`} style={{ left: `${(i * 3.9) % 100}%`, animationDuration: `${0.7 + (i % 4) * 0.18}s`, animationDelay: `${-(i % 9) * 0.25}s` }} />
      ))}
      {fx === "storm" && <span className="wx-flash" />}
    </div>
  );
}

export function InkWeather() {
  const [data, setData] = useState(null);
  const [place, setPlace] = useState("");
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");

  const load = async (lat, lon, name) => {
    try {
      const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code,wind_speed_10m,apparent_temperature&daily=weather_code,temperature_2m_max,temperature_2m_min&timezone=auto&forecast_days=4&temperature_unit=fahrenheit&wind_speed_unit=mph`);
      const j = await r.json();
      setData(j); setPlace(name); setErr("");
    } catch { setErr("Could not load weather."); }
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => load(p.coords.latitude.toFixed(3), p.coords.longitude.toFixed(3), "Your location"),
        () => load(40.7128, -74.006, "New York, US"),
        { timeout: 5000 }
      );
    } else load(40.7128, -74.006, "New York, US");
  }, []);

  const search = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    try {
      const r = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=1`);
      const j = await r.json();
      if (!j.results?.length) { setErr("City not found."); return; }
      const c = j.results[0];
      load(c.latitude, c.longitude, `${c.name}, ${c.country_code}`);
      setQ("");
    } catch { setErr("Search failed."); }
  };

  const cur = data?.current;
  const curWx = cur ? wx(cur.weather_code) : null;

  return (
    <div className="bg-white border border-gray-200 shadow-sm" data-testid="ink-weather">
      <div className="flex items-center justify-between px-5 py-3 bg-pti-black text-white">
        <div className="font-display text-lg font-black tracking-tight">Ink<span className="text-pti-red">Weather</span></div>
        <span className="text-[10px] uppercase tracking-widest text-neutral-400">Newsroom Conditions</span>
      </div>
      <div className="p-5">
        <form onSubmit={search} className="relative mb-4">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a city…" aria-label="Search a city"
            data-testid="weather-search" className="w-full border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-pti-red outline-none" />
        </form>

        {err && <p role="alert" className="text-sm text-pti-red font-semibold">{err}</p>}

        {cur && (
          <>
            <div className={`wx-stage wx-${fxFor(curWx?.label)} relative overflow-hidden rounded-lg text-white`} data-testid="weather-stage">
              <WeatherFX fx={fxFor(curWx?.label)} />
              <div className="relative z-10 p-5">
                <div className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-neutral-300"><MapPin size={12} className="text-pti-red" /> {place}</div>
                <div className="flex items-end gap-4 mt-2">
                  {curWx && <curWx.Icon size={54} className="text-white drop-shadow-[0_2px_10px_rgba(239,24,49,.5)]" aria-hidden="true" strokeWidth={1.5} />}
                  <div>
                    <div className="font-display text-6xl font-black leading-none drop-shadow-[0_3px_14px_rgba(0,0,0,.55)]">{Math.round(cur.temperature_2m)}°<span className="text-2xl align-top">F</span></div>
                    <div className="text-sm font-semibold text-neutral-200">{curWx?.label}</div>
                  </div>
                </div>
                <div className="flex gap-4 mt-3 text-xs text-neutral-300 font-medium">
                  <span>Feels {Math.round(cur.apparent_temperature)}°</span>
                  <span className="flex items-center gap-1"><Wind size={12} /> {Math.round(cur.wind_speed_10m)} mph</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-5 border-t border-gray-100 pt-4">
              {data.daily.time.slice(0, 4).map((t, i) => {
                const w = wx(data.daily.weather_code[i]);
                return (
                  <div key={t} className="text-center">
                    <div className="text-[11px] font-bold uppercase text-gray-500">{i === 0 ? "Today" : day(t)}</div>
                    <w.Icon size={22} className="mx-auto my-1 text-pti-black" aria-hidden="true" strokeWidth={1.5} />
                    <div className="text-xs font-bold">{Math.round(data.daily.temperature_2m_max[i])}°</div>
                    <div className="text-[11px] text-gray-400">{Math.round(data.daily.temperature_2m_min[i])}°</div>
                  </div>
                );
              })}
            </div>
            {cur.time && (
              <div className="text-[10px] text-gray-400 mt-4 pt-3 border-t border-gray-100" data-testid="weather-updated">
                Updated {new Date(cur.time).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })} local · live data · Open-Meteo
              </div>
            )}
          </>
        )}
        {!cur && !err && <p className="text-gray-400 text-sm py-6 text-center">Loading conditions…</p>}
      </div>
    </div>
  );
}
